import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "ftour-step" }
const _hoisted_3 = { class: "ftour-title" }
const _hoisted_4 = { class: "ftour-desc" }
const _hoisted_5 = { class: "ftour-actions" }
const _hoisted_6 = { class: "ftour-nav" }
const _hoisted_7 = ["disabled"]

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { BACK_LAYER, useBackCloseable } from '@/composables/useBackCloseable'
import { useFeatureTour } from '@/composables/useFeatureTour'


export default /*@__PURE__*/_defineComponent({
  __name: 'FeatureTourOverlay',
  setup(__props) {

const route = useRoute()
const {
  active,
  current,
  stepIndex,
  total,
  isFirst,
  isLast,
  pageTitle,
  hole,
  nextStep,
  prevStep,
  stopTour,
  refreshHole,
} = useFeatureTour()

useBackCloseable(BACK_LAYER.featureTour, () => active.value, () => {
  stopTour()
})

const cardEl = ref<HTMLElement | null>(null)
const cardH = ref(220)

const measureCard = () => {
  const h = cardEl.value?.offsetHeight || 0
  if (h > 0) cardH.value = h
}

const cardStyle = computed(() => {
  if (!hole.value.ready || typeof window === 'undefined') return {}
  const gap = 12
  const pad = 12
  const vw = window.innerWidth
  const vh = window.innerHeight
  const cardW = Math.min(360, vw - 24)
  const h = cardH.value
  const holeTop = hole.value.top
  const holeBottom = hole.value.top + hole.value.height
  const spaceBelow = vh - holeBottom
  const spaceAbove = holeTop
  const holeIsLow = holeBottom > vh * 0.68
  const holeIsHigh = holeTop < vh * 0.22

  let placeBelow = spaceBelow >= h + gap
  if (holeIsLow && spaceAbove >= Math.min(h + gap, 96)) placeBelow = false
  else if (holeIsHigh && spaceBelow >= Math.min(h + gap, 96)) placeBelow = true
  else if (!placeBelow && spaceAbove < h + gap) placeBelow = spaceBelow >= spaceAbove

  let top = placeBelow ? holeBottom + gap : holeTop - h - gap
  top = Math.max(pad, Math.min(top, vh - h - pad))

  const overlaps = top < holeBottom - 4 && top + h > holeTop + 4
  if (overlaps) {
    if (spaceAbove >= spaceBelow) {
      top = Math.max(pad, holeTop - h - gap)
    } else {
      top = Math.min(holeBottom + gap, vh - h - pad)
    }
    top = Math.max(pad, Math.min(top, vh - h - pad))
  }

  let left = hole.value.left + hole.value.width / 2 - cardW / 2
  left = Math.max(pad, Math.min(left, vw - cardW - pad))

  return {
    top: `${top}px`,
    left: `${left}px`,
    width: `${cardW}px`,
  }
})

const onKey = (event: KeyboardEvent) => {
  if (!active.value) return
  if (event.key === 'Escape') stopTour()
  if (event.key === 'ArrowRight' || event.key === 'Enter') nextStep()
  if (event.key === 'ArrowLeft') prevStep()
}

watch(
  () => [current.value?.name, hole.value.ready, hole.value.top, hole.value.height],
  async () => {
    await nextTick()
    measureCard()
  }
)

watch(
  () => route.fullPath,
  (next, prev) => {
    if (!prev || next === prev) return
    if (active.value) stopTour()
  }
)

let cardObserver: ResizeObserver | null = null

onMounted(() => {
  window.addEventListener('resize', refreshHole)
  window.addEventListener('scroll', refreshHole, true)
  window.addEventListener('keydown', onKey)
  if (typeof ResizeObserver !== 'undefined') {
    cardObserver = new ResizeObserver(() => measureCard())
    watch(cardEl, (el, prev) => {
      if (prev) cardObserver?.unobserve(prev)
      if (el) {
        cardObserver?.observe(el)
        measureCard()
      }
    }, { immediate: true })
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', refreshHole)
  window.removeEventListener('scroll', refreshHole, true)
  window.removeEventListener('keydown', onKey)
  cardObserver?.disconnect()
  cardObserver = null
  if (active.value) stopTour()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "ftour" }, {
      default: _withCtx(() => [
        (_unref(active) && _unref(current))
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "ftour",
              role: "dialog",
              "aria-modal": "true",
              "aria-label": `${_unref(pageTitle)} 화면 안내`
            }, [
              _createElementVNode("button", {
                type: "button",
                class: _normalizeClass(["ftour-catch", { 'is-solid': !_unref(hole).ready }]),
                "aria-label": "안내 닫기",
                onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(stopTour) && _unref(stopTour)(...args)))
              }, null, 2),
              (_unref(hole).ready)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "ftour-hole",
                    style: _normalizeStyle({
            top: `${_unref(hole).top}px`,
            left: `${_unref(hole).left}px`,
            width: `${_unref(hole).width}px`,
            height: `${_unref(hole).height}px`,
          })
                  }, null, 4))
                : _createCommentVNode("", true),
              _createElementVNode("div", {
                ref_key: "cardEl",
                ref: cardEl,
                class: _normalizeClass(["ftour-card", { 'is-center': !_unref(hole).ready }]),
                style: _normalizeStyle(cardStyle.value)
              }, [
                _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(stepIndex) + 1) + " / " + _toDisplayString(_unref(total)), 1),
                _createElementVNode("h3", _hoisted_3, _toDisplayString(_unref(current).name), 1),
                _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(current).description), 1),
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "ftour-ghost",
                    onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(stopTour) && _unref(stopTour)(...args)))
                  }, "건너뛰기"),
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "ftour-btn",
                      disabled: _unref(isFirst),
                      onClick: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(prevStep) && _unref(prevStep)(...args)))
                    }, "이전", 8, _hoisted_7),
                    _createElementVNode("button", {
                      type: "button",
                      class: "ftour-btn is-primary",
                      onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(nextStep) && _unref(nextStep)(...args)))
                    }, _toDisplayString(_unref(isLast) ? '완료' : '다음'), 1)
                  ])
                ])
              ], 6)
            ], 8, _hoisted_1))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})